<script lang="ts">
  import { route } from "@mateothegreat/svelte5-router";
  import { ArrowRight } from "lucide-svelte";
  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/tools"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Tools", url: "https://0xfire.com/tools" },
    ]),
  ));
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-orange-900 selection:text-white">
  <div class="max-w-4xl mx-auto px-4 py-20">
    <div class="text-center mb-16">
      <div class="badge badge-outline rounded-none uppercase tracking-widest font-mono text-xs text-orange-500 border-orange-500/50 p-3 mb-6">
        Tools
      </div>
      <h1 class="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
        Free <span class="text-orange-500">Trading Tools</span>
      </h1>
      <p class="text-xl text-gray-500 max-w-xl mx-auto">
        Explore live market data, query historical trades, and analyze on-chain activity.
      </p>
    </div>

    <div class="grid md:grid-cols-2 gap-6">
      <!-- Solana Tools -->
      <a
        use:route
        href="/tools/solana"
        class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-violet-900/40"
      >
        <div class="h-1 bg-violet-500 -mx-8 mb-8"></div>
        <h3 class="text-xl font-bold text-white mb-2">Solana</h3>
        <p class="text-sm text-gray-500 mb-6 leading-relaxed">
          Live trade streaming, historic trade queries, candlestick charts with technical indicators, pool analytics, and wallet tracking.
        </p>
        <span class="text-xs font-mono text-violet-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Open tools <ArrowRight class="size-3" />
        </span>
      </a>

      <!-- Polymarket Tools -->
      <a
        use:route
        href="/tools/polymarket"
        class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-sky-900/40"
      >
        <div class="h-1 bg-sky-500 -mx-8 mb-8"></div>
        <h3 class="text-xl font-bold text-white mb-2">Polymarket</h3>
        <p class="text-sm text-gray-500 mb-6 leading-relaxed">
          Real-time orderbook streaming for active Polymarket markets. Watch bids, asks, and volume flow live.
        </p>
        <span class="text-xs font-mono text-sky-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Open tools <ArrowRight class="size-3" />
        </span>
      </a>
      <!-- Wrap/Unwrap SOL -->
      <a
        use:route
        href="/tools/solana/wrap-sol"
        class="group block bg-gray-950/40 border border-gray-800/60 rounded-sm p-8 pt-0 overflow-hidden transition-colors hover:border-orange-900/40"
      >
        <div class="h-1 bg-orange-500 -mx-8 mb-8"></div>
        <h3 class="text-xl font-bold text-white mb-2">Wrap / Unwrap SOL</h3>
        <p class="text-sm text-gray-500 mb-6 leading-relaxed">
          Convert between SOL and Wrapped SOL (wSOL). Connect your wallet to wrap or unwrap instantly.
        </p>
        <span class="text-xs font-mono text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Open tool <ArrowRight class="size-3" />
        </span>
      </a>
    </div>

    <p class="text-center text-gray-700 text-xs font-mono mt-12">More tools on the way</p>

    <!-- About section (SEO) -->
    <div class="max-w-3xl mx-auto mt-16 border-t border-gray-800 pt-10">
      <h2 class="text-xl font-bold text-white mb-4 tracking-tight">About 0xfire Tools</h2>
      <div class="space-y-3 text-sm text-gray-400 leading-relaxed">
        <p>
          All tools on this page are free to use with no sign-up required. They are powered by the <a use:route href="/products/sailfish" class="text-orange-500 hover:underline">Sailfish API</a>, 0xfire's real-time trading data infrastructure.
        </p>
        <p>
          The Solana tools provide live trade streaming across Raydium, PumpFun, PumpSwap, and Meteora, historic trade data queries with candlestick charting, and pool graduation tracking for PumpFun and Raydium LaunchLab. The Polymarket tool streams live orderbooks for prediction markets.
        </p>
        <p>
          For full API access, including filtered WebSocket streaming, custom queries, and higher rate limits, see <a use:route href="/products/sailfish" class="text-orange-500 hover:underline">Sailfish subscriptions</a> starting at $75/month.
        </p>
      </div>
    </div>

    <!-- Related Links -->
    <div class="max-w-3xl mx-auto mt-10">
      <p class="text-xs font-mono text-gray-600 uppercase tracking-widest mb-4">Related</p>
      <div class="flex flex-wrap gap-3">
        <a use:route href="/products/sailfish" class="text-sm text-gray-500 hover:text-white transition-colors">Sailfish API</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/products/sailfish/docs" class="text-sm text-gray-500 hover:text-white transition-colors">API Docs</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/services" class="text-sm text-gray-500 hover:text-white transition-colors">Bot Development</a>
      </div>
    </div>
  </div>
</div>
